import type { CartAvailabilitySnapshot } from '@/lib/microsite/cartAvailability'

/**
 * El checkout espera esta respuesta con "Siguiente" en spinner. Pasado este tiempo
 * el carrito sigue como está: una ayuda no puede trabar el pago.
 */
const TIMEOUT_MS = 4000

/**
 * Pide a /api/online-orders/cart-availability el estado VIVO de los platos del
 * carrito.
 *
 * Devuelve null ante cualquier cosa que no sea un menú fresco y completo, a
 * tiempo. La re-verificación es una ayuda para el cliente, no la puerta (/session
 * vuelve a validar antes de cobrar), y quitar líneas por un error de red sería peor
 * que no avisar. `fetchFn` y `timeoutMs` son inyectables solo para tests, como
 * `fetchFn` en requestDeliveryQuote.
 */
export async function requestCartAvailability(
  locationId: string,
  itemIds: readonly string[],
  fetchFn: typeof fetch = fetch,
  timeoutMs: number = TIMEOUT_MS,
): Promise<CartAvailabilitySnapshot | null> {
  const ids = [...new Set(itemIds)]
  if (ids.length === 0) return null

  let res: Response
  try {
    res = await fetchFn('/api/online-orders/cart-availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locationId, itemIds: ids }),
      // También corta la lectura del cuerpo: el `res.json()` de abajo cae en su catch.
      signal: AbortSignal.timeout(timeoutMs),
    })
  } catch {
    return null
  }
  if (!res.ok) return null

  const data = (await res.json().catch(() => null)) as { timezone?: unknown; items?: unknown } | null
  // Sin la lista no hay menú: jamás leerlo como "no queda nada de lo que pediste".
  if (!data || !Array.isArray(data.items)) return null

  return {
    timezone: typeof data.timezone === 'string' ? data.timezone : null,
    items: data.items as CartAvailabilitySnapshot['items'],
  }
}
